import React, {useContext, useEffect, useState} from 'react';
import {CustomColumnComponentProps} from '@src/@types/components/ControlledList';
import {IModelFileToImportModel} from '@src/@interfaces/models/ModelFileToImport/IModelFileToImportModel';
import SelectableColumn from '@src/components/List/columns/SelectableColumn';
import useUploader from '@src/hooks/useUploader';
import {Events} from '@src/@interfaces/services/Http/IAsyncUploader';
import Translate from '@src/components/Translate/Translate';
import CircularProgressBar from '@src/components/ProgressBar/Circular/CircularProgressBar';
import {humanFileSize} from '@src/helpers/humanFileSize';
import {IAsyncUploaderQueuedFile} from '@src/@interfaces/services/Http/IAsyncUploaderQueuedFile';
import {IAsyncUploaderFileMeta} from '@src/@interfaces/services/Http/IAsyncUploaderFileMeta';
import {IImportConfig} from '@src/@interfaces/components/Import/IImportConfig';
import {LocaleContext} from '@src/contexts/LocaleContext';
import trans from '@src/helpers/trans';

function ProgressBar(props: Partial<React.ComponentProps<typeof CircularProgressBar>>) {
    return <CircularProgressBar
            size={20}
            color="primary"
            className="me-2"
            shouldSpin={false}
            {...props}
    />;
}

function WaitingForFileStatus(props: CustomColumnComponentProps<IModelFileToImportModel, {
    item: IAsyncUploaderQueuedFile,
}>) {
    const {item} = props;
    const meta = item.getMeta();
    const percent = item.getProgress();

    return <div className="d-flex align-items-center">
        <ProgressBar progress={percent}/>
        <div>
            <Translate message="List:ImportRecords.list.statuses.uploading"/>
            <div className="text-muted">{humanFileSize(meta.sentBytes)} / {humanFileSize(meta.totalBytes)}</div>
        </div>
    </div>;
}

function WaitingForProcessing() {
    return <div className="d-flex align-items-center">
        <ProgressBar shouldSpin={true} color="success" transparentTrack/>
        <Translate message="List:ImportRecords.list.statuses.waiting-for-processing"/>
    </div>;
}

export default function StatusColumn(props: CustomColumnComponentProps<IModelFileToImportModel, {
    controllerName: string,
    config: IImportConfig,
}>) {
    const hash = props.model.temp_file_hash;
    const {locale} = useContext(LocaleContext);
    const {getItemByServerHash, listen} = useUploader();
    const [, setLastEventFiredAt] = useState(0);
    const [uploaded, setUploaded] = useState(false);

    useEffect(() => {
        const subscription = listen<any | {
            uniqueId: string,
            meta: IAsyncUploaderFileMeta
        }>(Events.ALL, (event, payload) => {
            setLastEventFiredAt(Date.now());

            if (
                    event.name === Events.COMPLETE &&
                    payload != null &&
                    typeof payload === 'object' &&
                    !Array.isArray(payload) &&
                    'meta' in payload &&
                    payload.meta != null &&
                    typeof payload.meta === 'object' &&
                    'serverHash' in payload.meta &&
                    payload.meta.serverHash === hash
            ) {
                setUploaded(true);
            }
        });

        return () => subscription.unsubscribe();
    }, [hash, listen]);

    const item = hash ? getItemByServerHash(hash) : null;
    const status = props.model.status;
    if (item && status === 'waiting-for-temp-file') {
        return <WaitingForFileStatus {...props} item={item}/>;
    }

    if (uploaded && status === 'waiting-for-temp-file') {
        return <WaitingForProcessing/>;
    }

    return <SelectableColumn<IModelFileToImportModel>
            {...props as any}
            forceValue={trans(`List:ImportRecords.list.statuses.${status}`, locale)}
    />;
}
